const DB_NAME = "bb-service-tech-cache-v1";
const DB_VERSION = 1;
const MANIFEST_STORE = "manifest";
const PENDING_STORE = "pending_check_ins";

import type { ServiceCheckInMilestone, ServiceTechManifest } from "@/lib/service/types";

export type PendingServiceTechCheckIn = {
  id: string;
  event_id: string;
  milestone: ServiceCheckInMilestone;
  notes: string;
  evidence_url: string | null;
  queued_at: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(MANIFEST_STORE)) {
        db.createObjectStore(MANIFEST_STORE);
      }
      if (!db.objectStoreNames.contains(PENDING_STORE)) {
        db.createObjectStore(PENDING_STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
  });
}

export async function readCachedServiceTechManifest(): Promise<ServiceTechManifest | null> {
  if (typeof indexedDB === "undefined") return null;
  const db = await openDb();
  const tx = db.transaction(MANIFEST_STORE, "readonly");
  const store = tx.objectStore(MANIFEST_STORE);
  const manifest = await new Promise<ServiceTechManifest | null>((resolve, reject) => {
    const req = store.get("current");
    req.onsuccess = () => resolve((req.result as ServiceTechManifest | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return manifest;
}

export async function writeCachedServiceTechManifest(manifest: ServiceTechManifest): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  const db = await openDb();
  const tx = db.transaction(MANIFEST_STORE, "readwrite");
  tx.objectStore(MANIFEST_STORE).put(manifest, "current");
  await txDone(tx);
  db.close();
}

export async function listPendingServiceTechCheckIns(): Promise<PendingServiceTechCheckIn[]> {
  if (typeof indexedDB === "undefined") return [];
  const db = await openDb();
  const tx = db.transaction(PENDING_STORE, "readonly");
  const items = await new Promise<PendingServiceTechCheckIn[]>((resolve, reject) => {
    const req = tx.objectStore(PENDING_STORE).getAll();
    req.onsuccess = () => resolve((req.result as PendingServiceTechCheckIn[]) ?? []);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return items.sort((a, b) => a.queued_at.localeCompare(b.queued_at));
}

export async function enqueuePendingServiceTechCheckIn(
  item: Omit<PendingServiceTechCheckIn, "id" | "queued_at"> & { id?: string; queued_at?: string }
): Promise<PendingServiceTechCheckIn> {
  const row: PendingServiceTechCheckIn = {
    id: item.id ?? crypto.randomUUID(),
    event_id: item.event_id,
    milestone: item.milestone,
    notes: item.notes ?? "",
    evidence_url: item.evidence_url ?? null,
    queued_at: item.queued_at ?? new Date().toISOString(),
  };
  if (typeof indexedDB === "undefined") return row;
  const db = await openDb();
  const tx = db.transaction(PENDING_STORE, "readwrite");
  tx.objectStore(PENDING_STORE).put(row);
  await txDone(tx);
  db.close();
  return row;
}

export async function removePendingServiceTechCheckIn(id: string): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  const db = await openDb();
  const tx = db.transaction(PENDING_STORE, "readwrite");
  tx.objectStore(PENDING_STORE).delete(id);
  await txDone(tx);
  db.close();
}

const MILESTONE_RANK: Record<ServiceCheckInMilestone, number> = {
  confirmed: 1,
  day_before: 2,
  en_route: 3,
  on_site: 4,
  complete: 5,
};

export function milestoneRank(m: ServiceCheckInMilestone): number {
  return MILESTONE_RANK[m];
}

export function applyCheckInToManifest(
  manifest: ServiceTechManifest,
  eventId: string,
  milestone: ServiceCheckInMilestone
): ServiceTechManifest {
  return {
    ...manifest,
    events: manifest.events.map((ev) =>
      ev.id === eventId ? { ...ev, check_in_status: milestone } : ev
    ),
  };
}
